import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeSelection, TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state'

// Loose ends are the writer's "fix it later" marks. Two live in the document
// itself: a pin (an inline chip standing in for something missing) and a span
// (a mark over text that needs another look). Typed tokens such as TK are the
// third kind: plain text that is listed as a loose end wherever it appears.
// Everything here is pure so the editor, background page edits, the Loose
// Ends page, and exports all read and change loose ends the same way.

export const PIN_NODE = 'looseEnd'
export const SPAN_MARK = 'looseEndSpan'

/** Stands in for a pin in a text block's flattened text. */
const PIN_CHAR = '￼'
const EXCERPT_CONTEXT = 70
const FLAGGED_MAX = 120
export const NOTE_MAX = 280

export type LooseEndShape = 'pin' | 'span' | 'token'

interface LooseEndAttrs {
  id: string
  note: string
  kind: string
  created: number
}

/** One loose end as found in a document. */
export interface FoundLooseEnd extends LooseEndAttrs {
  shape: LooseEndShape
  /** Text before, flagged, and after, for showing the loose end in context. */
  before: string
  flagged: string
  after: string
  /** Document ranges it covers; one for pins and tokens, one per text run for spans. */
  ranges: Array<{ from: number; to: number }>
}

export function newLooseEndId(): string {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    : Math.random().toString(36).slice(2, 14)
  return `le${random}`
}

export function isTokenId(id: string): boolean {
  return id.startsWith('tok:')
}

// ── Tokens ──────────────────────────────────────────────────────────────────

export type TokenSpec =
  | { type: 'word'; text: string }
  | { type: 'pair'; open: string; close: string }

/** Reads a token as written in settings: "XX" is a word, "[[ ]]" is a pair. */
export function parseTokenSpec(raw: string): TokenSpec | null {
  const trimmed = raw.trim()
  const pair = /^(\S+)\s+(\S+)$/.exec(trimmed)
  if (pair) return { type: 'pair', open: pair[1], close: pair[2] }
  if (/\s/.test(trimmed) || [...trimmed].length < 2) return null
  return { type: 'word', text: trimmed }
}

export function tokenSpecs(settings: { tkEnabled: boolean; customTokens: string[] }): TokenSpec[] {
  const specs: TokenSpec[] = settings.tkEnabled ? [{ type: 'word', text: 'TK' }] : []
  for (const raw of settings.customTokens) {
    const spec = parseTokenSpec(raw)
    if (spec && !(spec.type === 'word' && spec.text === 'TK' && settings.tkEnabled)) specs.push(spec)
  }
  return specs
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const WORD_EDGE_BEFORE = '(?<![\\p{L}\\p{N}_])'
const WORD_EDGE_AFTER = '(?![\\p{L}\\p{N}_])'

/** Matches a token anywhere in text. A pair captures its note in group 1. */
export function tokenPattern(spec: TokenSpec): RegExp {
  if (spec.type === 'pair') {
    return new RegExp(`${escapeRegExp(spec.open)}([^\\n${PIN_CHAR}]*?)${escapeRegExp(spec.close)}`, 'gu')
  }
  // TK is conventionally doubled (TKTK) to make it stand out further.
  const word = spec.text === 'TK' ? '(?:TK)+' : escapeRegExp(spec.text)
  return new RegExp(`${WORD_EDGE_BEFORE}${word}${WORD_EDGE_AFTER}`, 'gu')
}

/** Characters that end a word token as it is typed. */
const TOKEN_ENDINGS = ' .,;:!?…)]}"\'”’—–'

/**
 * Input rule pattern: the token just before the cursor, followed by the
 * character being typed. For a word, group 1 is the token and group 2 the
 * ending; for a pair, group 1 is the note.
 */
export function tokenInputPattern(spec: TokenSpec): RegExp {
  if (spec.type === 'pair') {
    return new RegExp(`${escapeRegExp(spec.open)}([^\\n${PIN_CHAR}]+?)${escapeRegExp(spec.close)}$`, 'u')
  }
  const word = spec.text === 'TK' ? '(?:TK)+' : escapeRegExp(spec.text)
  return new RegExp(`${WORD_EDGE_BEFORE}(${word})([${escapeRegExp(TOKEN_ENDINGS)}])$`, 'u')
}

// ── Scanning ────────────────────────────────────────────────────────────────

interface FlatBlock {
  /** Document position of the block's first inline child. */
  start: number
  text: string
}

/** A text block as one string, one character per position, pins as PIN_CHAR. */
function flattenBlock(block: ProseMirrorNode, start: number): FlatBlock {
  let text = ''
  block.forEach((child) => {
    if (child.isText) text += child.text
    else if (child.type.name === PIN_NODE) text += PIN_CHAR
    else text += ' '.repeat(child.nodeSize)
  })
  return { start, text }
}

function readable(text: string): string {
  return text.replace(new RegExp(PIN_CHAR, 'g'), 'TK').replace(/\s+/g, ' ')
}

function clip(text: string, max: number, fromEnd: boolean): string {
  if (text.length <= max) return text
  if (fromEnd) {
    const cut = text.slice(text.length - max)
    const space = cut.indexOf(' ')
    return `…${space > 0 && space < 20 ? cut.slice(space + 1) : cut}`
  }
  const cut = text.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${space > max - 20 ? cut.slice(0, space) : cut}…`
}

function contextBefore(text: string, index: number): string {
  return clip(readable(text.slice(0, index)).trimStart(), EXCERPT_CONTEXT, true)
}

function contextAfter(text: string, index: number): string {
  return clip(readable(text.slice(index)).trimEnd(), EXCERPT_CONTEXT, false)
}

function attrsOf(raw: any): LooseEndAttrs {
  return {
    id: typeof raw?.id === 'string' ? raw.id : '',
    note: typeof raw?.note === 'string' ? raw.note : '',
    kind: typeof raw?.kind === 'string' ? raw.kind : '',
    created: Number.isFinite(raw?.created) ? raw.created : 0,
  }
}

/**
 * Every loose end in a document, in reading order. Tokens are numbered by
 * occurrence, so their ids are stable only until the text around them changes.
 */
export function scanLooseEnds(doc: ProseMirrorNode, specs: TokenSpec[] = []): FoundLooseEnd[] {
  const found: FoundLooseEnd[] = []
  const spans = new Map<string, FoundLooseEnd>()
  const patterns = specs.map(tokenPattern)
  let tokenIndex = 0

  doc.descendants((block, pos) => {
    if (!block.isTextblock) return true
    if (block.type.spec.code) return false
    const flat = flattenBlock(block, pos + 1)
    const entries: Array<{ at: number; item: FoundLooseEnd }> = []

    let offset = 0
    block.forEach((child) => {
      const from = flat.start + offset
      if (child.type.name === PIN_NODE) {
        const attrs = attrsOf(child.attrs)
        entries.push({
          at: offset,
          item: {
            ...attrs,
            shape: 'pin',
            before: contextBefore(flat.text, offset),
            flagged: '',
            after: contextAfter(flat.text, offset + 1),
            ranges: [{ from, to: from + 1 }],
          },
        })
      } else if (child.isText) {
        const mark = child.marks.find((m) => m.type.name === SPAN_MARK)
        if (mark) {
          const attrs = attrsOf(mark.attrs)
          const end = offset + child.nodeSize
          const existing = spans.get(attrs.id)
          if (existing) {
            const last = existing.ranges[existing.ranges.length - 1]
            const contiguous = last.to === from
            if (contiguous) last.to = from + child.nodeSize
            else existing.ranges.push({ from, to: from + child.nodeSize })
            existing.flagged += (contiguous ? '' : ' ') + child.text
            existing.after = contextAfter(flat.text, end)
          } else {
            const item: FoundLooseEnd = {
              ...attrs,
              shape: 'span',
              before: contextBefore(flat.text, offset),
              flagged: child.text || '',
              after: contextAfter(flat.text, end),
              ranges: [{ from, to: from + child.nodeSize }],
            }
            spans.set(attrs.id, item)
            entries.push({ at: offset, item })
          }
        }
      }
      offset += child.nodeSize
    })

    for (const [index, pattern] of patterns.entries()) {
      const spec = specs[index]
      pattern.lastIndex = 0
      for (const match of flat.text.matchAll(pattern)) {
        const at = match.index ?? 0
        const note = spec.type === 'pair' ? (match[1] || '').trim() : ''
        entries.push({
          at,
          item: {
            id: '',
            note,
            kind: '',
            created: 0,
            shape: 'token',
            before: contextBefore(flat.text, at),
            flagged: match[0],
            after: contextAfter(flat.text, at + match[0].length),
            ranges: [{ from: flat.start + at, to: flat.start + at + match[0].length }],
          },
        })
      }
    }

    entries.sort((a, b) => a.at - b.at)
    for (const { item } of entries) {
      if (item.shape === 'token') item.id = `tok:${tokenIndex++}:${item.flagged}`
      found.push(item)
    }
    return false
  })

  for (const span of spans.values()) span.flagged = clip(readable(span.flagged).trim(), FLAGGED_MAX, false)
  return found
}

/** Looks up one loose end by id; tokens by their occurrence id. */
export function findLooseEnd(doc: ProseMirrorNode, id: string, specs: TokenSpec[] = []): FoundLooseEnd | null {
  return scanLooseEnds(doc, isTokenId(id) ? specs : []).find((item) => item.id === id) ?? null
}

/** Pins sharing an id with an earlier pin, as left by pasting a copy. */
export function duplicatePinPositions(doc: ProseMirrorNode): number[] {
  const seen = new Set<string>()
  const duplicates: number[] = []
  doc.descendants((node, pos) => {
    if (node.type.name !== PIN_NODE) return true
    const id = node.attrs.id
    if (!id || seen.has(id)) duplicates.push(pos)
    else seen.add(id)
    return false
  })
  return duplicates
}

// ── Changing loose ends ─────────────────────────────────────────────────────

export type LooseEndAction =
  /** Done with it. A pin or token can be replaced by the text it stood in for. */
  | { type: 'resolve'; fill?: string }
  | { type: 'update'; note?: string; kind?: string }

/** Deletes a range, taking one of two spaces that would be left side by side with it. */
export function deleteCollapsingSpace(tr: Transaction, from: number, to: number) {
  const doc = tr.doc
  const before = from > 0 ? doc.textBetween(from - 1, from, '\n', '\n') : ''
  const after = to < doc.content.size ? doc.textBetween(to, to + 1, '\n', '\n') : ''
  if (before === ' ' && (after === ' ' || /^[.,;:!?…)\]}”’]$/.test(after))) tr.delete(from - 1, to)
  else if (after === ' ' && (before === '' || before === '\n' || /^[(\[{“‘]$/.test(before))) tr.delete(from, to + 1)
  else tr.delete(from, to)
}

/**
 * Applies an action to one loose end in a transaction. Returns false when the
 * loose end is not in the document.
 */
export function applyLooseEndAction(tr: Transaction, id: string, action: LooseEndAction, specs: TokenSpec[] = []): boolean {
  const item = findLooseEnd(tr.doc, id, specs)
  if (!item) return false
  const schema = tr.doc.type.schema

  if (action.type === 'update') {
    const patch: Partial<LooseEndAttrs> = {}
    if (action.note !== undefined) patch.note = action.note.slice(0, NOTE_MAX)
    if (action.kind !== undefined) patch.kind = action.kind
    if (item.shape === 'pin') {
      const pos = item.ranges[0].from
      const node = tr.doc.nodeAt(pos)
      if (!node) return false
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...patch })
    } else if (item.shape === 'span') {
      const type = schema.marks[SPAN_MARK]
      const attrs = { id: item.id, note: item.note, kind: item.kind, created: item.created, ...patch }
      for (const range of item.ranges) {
        tr.removeMark(range.from, range.to, type)
        tr.addMark(range.from, range.to, type.create(attrs))
      }
    } else {
      return false
    }
    return true
  }

  const fill = action.fill?.trim() ? action.fill : ''
  if (item.shape === 'span') {
    const type = schema.marks[SPAN_MARK]
    for (const range of item.ranges) tr.removeMark(range.from, range.to, type)
    return true
  }

  const { from, to } = item.ranges[0]
  if (fill) {
    const marks = item.shape === 'pin'
      ? tr.doc.nodeAt(from)?.marks ?? []
      : tr.doc.resolve(from + 1).marks()
    tr.replaceWith(from, to, schema.text(fill, marks))
  } else {
    deleteCollapsingSpace(tr, from, to)
  }
  return true
}

/** Selects a loose end so the editor can scroll to it. */
export function selectLooseEnd(tr: Transaction, item: FoundLooseEnd): Transaction {
  const first = item.ranges[0]
  const last = item.ranges[item.ranges.length - 1]
  if (item.shape === 'pin') return tr.setSelection(NodeSelection.create(tr.doc, first.from))
  return tr.setSelection(TextSelection.create(tr.doc, first.from, last.to))
}

/** The pin or span the selection is on, if any. */
export function looseEndAtSelection(state: EditorState): FoundLooseEnd | null {
  const { selection, doc } = state
  if (selection instanceof NodeSelection && selection.node.type.name === PIN_NODE) {
    return findLooseEnd(doc, selection.node.attrs.id)
  }
  const mark = selection.$from.marks().find((m) => m.type.name === SPAN_MARK)
    ?? (selection.empty ? null : doc.resolve(Math.min(selection.from + 1, doc.content.size)).marks().find((m) => m.type.name === SPAN_MARK))
  return mark ? findLooseEnd(doc, mark.attrs.id) : null
}

// ── Quick Edit ──────────────────────────────────────────────────────────────
// A few sentences around a loose end, as plain text the writer can change.
// Pins appear as "TK"; replacing that TK fills the pin in. Only the part of
// the passage that actually changed is written back, so formatting and other
// loose ends in the untouched text stay as they were.

const PASSAGE_SIDE_MIN = 40
const PASSAGE_SIDE_MAX = 300
const PIN_TEXT = 'TK'

export interface LooseEndPassage {
  text: string
  /** Where the loose end sits in `text`, to select it when editing starts. */
  select: [number, number]
  /** Document position for each offset in `text` (and one past its end). */
  positions: number[]
  /** Pins inside `text`, by offset; each is two characters long. */
  pins: number[]
}

function sentenceStartBefore(text: string, index: number): number {
  const ends = /[.!?…]["”’)\]]*\s+/g
  let start = 0
  let match: RegExpExecArray | null
  while ((match = ends.exec(text)) && match.index + match[0].length <= index) start = match.index + match[0].length
  return start
}

function sentenceEndAfter(text: string, index: number): number {
  const ends = /[.!?…]["”’)\]]*(?=\s|$)/g
  ends.lastIndex = index
  const match = ends.exec(text)
  return match ? match.index + match[0].length : text.length
}

/** The passage around a loose end, or null if it cannot be edited as plain text. */
export function readPassage(doc: ProseMirrorNode, id: string, specs: TokenSpec[] = []): LooseEndPassage | null {
  const item = findLooseEnd(doc, id, specs)
  if (!item) return null
  const $start = doc.resolve(item.ranges[0].from)
  const block = $start.parent
  const blockStart = $start.start()
  const last = item.ranges[item.ranges.length - 1]
  if (last.to > blockStart + block.content.size) return null

  // The block as text, with a barrier (\0) at anything that is not text or a pin.
  let text = ''
  const positions: number[] = []
  const pins: number[] = []
  let pos = blockStart
  block.forEach((child) => {
    if (child.isText) {
      for (let i = 0; i < child.nodeSize; i++) positions.push(pos + i)
      text += child.text
    } else if (child.type.name === PIN_NODE) {
      pins.push(text.length)
      positions.push(pos, pos)
      text += PIN_TEXT
    } else {
      for (let i = 0; i < child.nodeSize; i++) positions.push(pos + i)
      text += '\0'.repeat(child.nodeSize)
    }
    pos += child.nodeSize
  })
  positions.push(pos)

  const offsetOf = (docPos: number) => {
    const index = positions.indexOf(docPos)
    return index < 0 ? positions.length - 1 : index
  }
  const start = offsetOf(item.ranges[0].from)
  const end = item.shape === 'pin' ? start + PIN_TEXT.length : offsetOf(last.to)

  // The sentence it is in, widened by a sentence on either side when that
  // leaves too little context, never past a barrier or the length cap.
  let from = sentenceStartBefore(text, start)
  if (start - from < PASSAGE_SIDE_MIN && from > 0) from = sentenceStartBefore(text, Math.max(0, from - 1))
  let to = sentenceEndAfter(text, end)
  if (to - end < PASSAGE_SIDE_MIN && to < text.length) to = sentenceEndAfter(text, Math.min(text.length, to + 1))
  const barrierBefore = text.lastIndexOf('\0', start - 1)
  const barrierAfter = text.indexOf('\0', end)
  from = Math.max(from, barrierBefore + 1, start - PASSAGE_SIDE_MAX)
  to = Math.min(to, barrierAfter < 0 ? text.length : barrierAfter, end + PASSAGE_SIDE_MAX)
  // A capped side ends at a word, and never inside a pin.
  if (from === start - PASSAGE_SIDE_MAX) from = Math.min(start, text.indexOf(' ', from) + 1 || from)
  if (to === end + PASSAGE_SIDE_MAX) to = Math.max(end, text.lastIndexOf(' ', to) || to)
  for (const pin of pins) {
    if (from > pin && from < pin + 2) from = pin
    if (to > pin && to < pin + 2) to = pin + 2
  }
  while (from < start && text[from] === ' ') from++
  while (to > end && text[to - 1] === ' ') to--

  return {
    text: text.slice(from, to),
    select: [start - from, end - from],
    positions: positions.slice(from, to + 1),
    pins: pins.filter((pin) => pin >= from && pin + 2 <= to).map((pin) => pin - from),
  }
}

export interface PassageEdit {
  changed: boolean
  /** The text that replaced the loose end, when the edit took it out. */
  replacedWith: string | null
}

/**
 * Writes an edited passage back. `select` is the loose end's place in the
 * passage; the result says whether the edit replaced it.
 */
export function applyPassageEdit(tr: Transaction, passage: LooseEndPassage, edited: string): PassageEdit {
  const next = edited.replace(/\s*\n\s*/g, ' ')
  const old = passage.text
  if (next === old) return { changed: false, replacedWith: null }

  let prefix = 0
  const limit = Math.min(old.length, next.length)
  while (prefix < limit && old[prefix] === next[prefix]) prefix++
  let suffix = 0
  while (suffix < limit - prefix && old[old.length - 1 - suffix] === next[next.length - 1 - suffix]) suffix++

  // Widen the change to whole pins; a pin is replaced whole or kept whole.
  let start = prefix
  let oldEnd = old.length - suffix
  for (const pin of passage.pins) {
    if (start > pin && start < pin + 2) start = pin
    if (oldEnd > pin && oldEnd < pin + 2) oldEnd = pin + 2
  }
  const newEnd = next.length - (old.length - oldEnd)
  const replacement = next.slice(start, newEnd)
  const from = passage.positions[start]
  const to = passage.positions[oldEnd]
  const schema = tr.doc.type.schema

  if (replacement) {
    const first = from < to ? tr.doc.nodeAt(from) : null
    const marks = first?.isText ? first.marks : tr.doc.resolve(from).marks()
    tr.replaceWith(from, to, schema.text(replacement, marks))
  } else {
    tr.delete(from, to)
  }

  const [selectStart, selectEnd] = passage.select
  const replaced = start <= selectStart && oldEnd >= selectEnd
  return { changed: true, replacedWith: replaced ? replacement.trim() : null }
}

// ── Exports ─────────────────────────────────────────────────────────────────

export const EXPORT_COMMENT_START = 'looseEndCommentStart'
export const EXPORT_COMMENT_END = 'looseEndCommentEnd'

function commentText(attrs: LooseEndAttrs): string {
  const note = attrs.note.trim() || 'TK'
  return attrs.kind ? `${attrs.kind}: ${note}` : note
}

/** Counts pins and spans in document JSON. */
export function countLooseEndsInJSON(json: any): number {
  const ids = new Set<string>()
  const walk = (node: any) => {
    if (!node || typeof node !== 'object') return
    if (node.type === PIN_NODE) ids.add(node.attrs?.id || `pin${ids.size}`)
    for (const mark of node.marks || []) if (mark.type === SPAN_MARK) ids.add(mark.attrs?.id || '')
    ;(node.content || []).forEach(walk)
  }
  walk(json)
  return ids.size
}

function collapseStrippedSpaces(content: any[]): any[] {
  const out: any[] = []
  for (const node of content) {
    const prev = out[out.length - 1]
    if (node.type === 'text' && prev?.type === 'text' && prev.text.endsWith(' ') && /^[ .,;:!?…)\]}”’]/.test(node.text)) {
      if (node.text.startsWith(' ')) node.text = node.text.slice(1)
      else prev.text = prev.text.slice(0, -1)
      if (!node.text) continue
    }
    out.push(node)
  }
  return out.filter((node) => node.type !== 'text' || node.text)
}

/**
 * Prepares loose ends in document JSON for an export. 'strip' removes them;
 * 'comments' turns each into a comment range the DOCX and ODT generators
 * write as a margin comment. The JSON passed in is not changed.
 */
export function prepareLooseEndsForExport(json: any, mode: 'strip' | 'comments'): any {
  const copy = JSON.parse(JSON.stringify(json ?? { type: 'doc', content: [] }))
  let number = 0
  const numbers = new Map<string, number>()
  const numberFor = (id: string) => {
    if (!numbers.has(id)) numbers.set(id, number++)
    return numbers.get(id)!
  }

  // Where each span's last text run is, so its comment closes there.
  const lastRun = new Map<string, any>()
  const findLastRuns = (node: any) => {
    for (const mark of node?.marks || []) if (mark.type === SPAN_MARK) lastRun.set(mark.attrs?.id, node)
    ;(node?.content || []).forEach(findLastRuns)
  }
  if (mode === 'comments') findLastRuns(copy)

  const opened = new Set<string>()
  const walk = (node: any) => {
    if (!Array.isArray(node?.content)) return
    const next: any[] = []
    let stripped = false
    for (const child of node.content) {
      if (child.type === PIN_NODE) {
        stripped = true
        if (mode === 'comments') {
          const attrs = attrsOf(child.attrs)
          const num = numberFor(attrs.id || `pin${number}`)
          const text = commentText(attrs)
          next.push({ type: EXPORT_COMMENT_START, attrs: { num, text, point: true } })
          next.push({ type: EXPORT_COMMENT_END, attrs: { num, text, point: true } })
        }
        continue
      }
      const span = child.marks?.find((mark: any) => mark.type === SPAN_MARK)
      if (span) {
        child.marks = child.marks.filter((mark: any) => mark.type !== SPAN_MARK)
        if (!child.marks.length) delete child.marks
        if (mode === 'comments') {
          const attrs = attrsOf(span.attrs)
          const num = numberFor(attrs.id)
          if (!opened.has(attrs.id)) {
            opened.add(attrs.id)
            next.push({ type: EXPORT_COMMENT_START, attrs: { num, text: commentText(attrs) } })
          }
          next.push(child)
          if (lastRun.get(attrs.id) === child) next.push({ type: EXPORT_COMMENT_END, attrs: { num, text: commentText(attrs) } })
          continue
        }
      }
      walk(child)
      next.push(child)
    }
    node.content = stripped && mode === 'strip' ? collapseStrippedSpaces(next) : next
  }
  walk(copy)
  return copy
}

